import { SupportTicketIdZod } from "@redotech/effect-api-shared/ids/zod";
import {
  CsatSortableField,
  SatisfactionResponseStatusTypes,
} from "@redotech/redo-support-common/support/csat-response";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import { SortDirection } from "@redotech/redo-views-common/table";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export type CsatResponse = z.infer<typeof csatResponseSchema>;

export const csatResponseSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  // v1 rows carry a Mongo conversation ObjectId; v2 rows carry a branded
  // SupportTicketId instead. Exactly one is present.
  conversation: zExt.objectId().optional(),
  supportTicketId: SupportTicketIdZod.optional(),
  rating: z.number(),
  comment: z.string(),
  channel: createEnum(ConversationPlatform),
  status: createEnum(SatisfactionResponseStatusTypes),
  createdAt: z.date(),
  updatedAt: z.date(),
  sendAt: z.date().optional(),
});

export const csatVariantStatsSchema = z.object({
  variantId: z.string(),
  name: z.string(),
  sent: z.number(),
  responded: z.number(),
  responseRate: z.number(),
  averageRating: z.number().optional(),
});

/** Per-arm results for the CSAT A/B test, read from the survey rows both send paths write. */
export type GetCsatVariantStatsInput = z.infer<
  typeof getCsatVariantStats.input
>;

export const getCsatVariantStats = {
  name: "getCsatVariantStats" as const,
  input: z.object({
    dateRangeStart: z.date().optional(),
    dateRangeEnd: z.date().optional(),
  }),
  output: z.array(csatVariantStatsSchema),
};

/**
 * The team's CSAT survey templates, each flagged with whether it actually
 * prints the survey link. `hasSurveyLink` is computed with the same check the
 * send path uses, so the settings page and the renderer never disagree.
 */
export const getCsatSurveyTemplates = {
  name: "getCsatSurveyTemplates" as const,
  input: z.object({}),
  output: z.array(
    z.object({ id: z.string(), name: z.string(), hasSurveyLink: z.boolean() }),
  ),
};

export type CsatSurveyTemplateSummary = z.infer<
  typeof getCsatSurveyTemplates.output
>[number];

/**
 * Creates the survey email template the merchant then designs in the email
 * builder. Support-permissioned: authoring a CSAT survey must not require
 * marketing access, which `createEmailTemplate` demands.
 */
export const createCsatSurveyTemplate = {
  name: "createCsatSurveyTemplate" as const,
  input: z.object({ name: z.string().min(1) }),
  output: z.object({ emailTemplateId: z.string() }),
};

export type CreateCsatSurveyTemplateInput = z.infer<
  typeof createCsatSurveyTemplate.input
>;

const analyticsCsatResponseSchema = csatResponseSchema.extend({
  customer: z.any(),
  assignee: zExt.objectId().optional(),
});

const csatAnalyticsFilters = z.object({
  platforms: z.array(createEnum(ConversationPlatform)).optional(),
  dateRangeStart: z.date(),
  dateRangeEnd: z.date(),
});

export const getCsatResponsesForTeam = {
  name: "getCsatResponsesForTeam" as const,
  input: z.object({
    pageContinue: z.string().optional(),
    pageSize: z.number(),
    pageStop: z.string().optional(),
    sort: z.object({
      direction: createEnum(SortDirection),
      key: createEnum(CsatSortableField),
    }),
    filters: csatAnalyticsFilters,
  }),
  output: z.object({
    data: z.array(analyticsCsatResponseSchema),
    pageNext: z.string().optional(),
  }),
};

export type GetCsatResponsesForTeamInput = z.infer<
  typeof getCsatResponsesForTeam.input
>;
export type GetCsatResponsesForTeamOutput = z.infer<
  typeof getCsatResponsesForTeam.output
>;

export const getCsatResponsesForTeamCount = {
  name: "getCsatResponsesForTeamCount" as const,
  input: z.object({ filters: csatAnalyticsFilters }),
  output: z.object({ count: z.number() }),
};

export type GetCsatResponsesForTeamCountInput = z.infer<
  typeof getCsatResponsesForTeamCount.input
>;
